import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Box, Typography, Button, Dialog, IconButton } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import 'leaflet/dist/leaflet.css';
import { GeoLocation } from '../../types';
import { DESIGN_TOKENS } from '../../styles/theme';
import { useI18n } from '../../i18n';

// Fix for default Leaflet marker icons in React
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface RouteMapProps {
  start?: GeoLocation;
  end?: GeoLocation;
  geojson?: any;
}

// Component to adjust map bounds to fit the route
function MapBounds({ start, end, geojson }: { start?: [number, number] | null, end?: [number, number] | null, geojson?: any }) {
  const map = useMap();
  React.useEffect(() => {
    let bounds = L.latLngBounds([]);

    if (start && end) {
      bounds.extend(start);
      bounds.extend(end);
    }

    if (geojson) {
      const geojsonLayer = L.geoJSON(geojson);
      bounds.extend(geojsonLayer.getBounds());
    }

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, start, end, geojson]);
  return null;
}

export const RouteMap: React.FC<RouteMapProps> = ({ start, end, geojson }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { t } = useI18n();

  const startCoords: [number, number] | null =
    start?.lat && start?.lng ? [Number(start.lat), Number(start.lng)] : null;
  const endCoords: [number, number] | null =
    end?.lat && end?.lng ? [Number(end.lat), Number(end.lng)] : null;

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const MapContent = () => (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {startCoords && (
        <Marker position={startCoords}>
          <Popup>
            <Typography variant="body2" fontWeight={600}>{t('maps.startLocation')}</Typography>
            <Typography variant="caption">{start?.city || start?.address}</Typography>
          </Popup>
        </Marker>
      )}
      {endCoords && (
        <Marker position={endCoords}>
          <Popup>
            <Typography variant="body2" fontWeight={600}>{t('maps.endLocation')}</Typography>
            <Typography variant="caption">{end?.city || end?.address}</Typography>
          </Popup>
        </Marker>
      )}
      {startCoords && endCoords && !geojson && (
        <Polyline
          positions={[startCoords, endCoords]}
          color={DESIGN_TOKENS.colors.primary}
          weight={4}
          opacity={0.7}
          dashArray="10, 10"
        />
      )}
      {geojson && (
        <GeoJSON
          data={geojson}
          style={{
            color: DESIGN_TOKENS.colors.primary,
            weight: 5,
            opacity: 0.8
          }}
        />
      )}
      <MapBounds start={startCoords} end={endCoords} geojson={geojson} />
    </>
  );

  if (!startCoords && !endCoords && !geojson) {
    return (
      <Box
        sx={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.02)',
          borderRadius: 2,
          border: '1px dashed rgba(0,0,0,0.1)',
          my: 2
        }}
      >
        <Typography color="text.secondary" variant="body2">
          {t('maps.noGeodata')}
        </Typography>
      </Box>
    );
  }

  const center: [number, number] = startCoords ? startCoords : (endCoords ? endCoords : [52.2297, 21.0122]);

  return (
    <>
      <Box
        sx={{
          height: 500,
          width: '100%',
          borderRadius: DESIGN_TOKENS.borderRadius.card,
          overflow: 'hidden',
          my: 2,
          border: `1px solid ${DESIGN_TOKENS.colors.border}`,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<FullscreenIcon />}
          onClick={handleToggleFullscreen}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: '#fff',
            },
          }}
        >
          {t('common.fullScreen')}
        </Button>
        <MapContainer
          center={center}
          zoom={6}
          style={{ height: '100%', width: '100%' }}
        >
          <MapContent />
        </MapContainer>
      </Box>

      <Dialog
        fullScreen
        open={isFullscreen}
        onClose={handleToggleFullscreen}
      >
        <Box sx={{ position: 'relative', height: '100vh', width: '100vw' }}>
          <IconButton
            onClick={handleToggleFullscreen}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 2000,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: '#fff',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <MapContainer
            center={center}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
          >
            <MapContent />
          </MapContainer>
        </Box>
      </Dialog>
    </>
  );
};
